import { useParams } from 'react-router-dom';
import { lazy, type ReactElement } from 'react';

export interface EmailSignatureRouteDefinition {
  capabilityPath: string;
  label: string;
  element: ReactElement;
}

export const EmailSignaturesDashboard = lazy(() => import('./pages/EmailSignatures'));
export const SignatureList = lazy(() =>
  import('./pages/SignatureList').then((module) => ({ default: module.SignatureList }))
);
export const SignatureTemplateEditorPage = lazy(() =>
  import('./pages/SignatureTemplateEditorPage').then((module) => ({
    default: module.SignatureTemplateEditorPage,
  }))
);
export const TemplateGallery = lazy(() =>
  import('./pages/TemplateGallery').then((module) => ({ default: module.TemplateGallery }))
);
export const CampaignsPage = lazy(() =>
  import('./pages/CampaignsPage').then((module) => ({ default: module.CampaignsPage }))
);
export const DeploymentPage = lazy(() =>
  import('./pages/DeploymentPage').then((module) => ({ default: module.DeploymentPage }))
);
export const TemplatesPage = lazy(() =>
  import('./pages/TemplatesPage').then((module) => ({ default: module.TemplatesPage }))
);
export const BGInfoPage = lazy(() =>
  import('./pages/BGInfoPage').then((module) => ({ default: module.BGInfoPage }))
);
export const OutOfOfficePage = lazy(() =>
  import('./pages/OutOfOfficePage').then((module) => ({ default: module.OutOfOfficePage }))
);
export const FontLibrary = lazy(() =>
  import('./pages/FontLibrary').then((module) => ({ default: module.FontLibrary }))
);
export const IrisDeploymentHealthPage = lazy(() =>
  import('./pages/IrisDeploymentHealthPage').then((module) => ({
    default: module.IrisDeploymentHealthPage,
  }))
);
export const IrisCustomersPage = lazy(() =>
  import('./pages/IrisCustomersPage').then((module) => ({ default: module.IrisCustomersPage }))
);
export const SignatureRichTextEditor = lazy(() =>
  import('./components/SignatureRichTextEditor').then((module) => ({
    default: module.SignatureRichTextEditor,
  }))
);

export const EMAIL_SIGNATURE_ROUTE_DEFINITIONS: readonly EmailSignatureRouteDefinition[] = [
  {
    capabilityPath: '/email-signatures',
    label: 'Email Signatures Dashboard',
    element: <EmailSignaturesDashboard />,
  },
  {
    capabilityPath: '/email-signatures/signatures',
    label: 'Signature List',
    element: <SignatureList />,
  },
  {
    capabilityPath: '/email-signatures/signatures/gallery',
    label: 'Template Gallery',
    element: <TemplateGallery />,
  },
  {
    capabilityPath: '/email-signatures/signatures/new',
    label: 'Signature Editor',
    element: <SignatureTemplateEditorPage />,
  },
  {
    capabilityPath: '/email-signatures/signatures/:templateId',
    label: 'Signature Editor',
    element: <SignatureTemplateEditorPage />,
  },
  {
    capabilityPath: '/email-signatures/campaigns',
    label: 'Campaigns',
    element: <CampaignsPage />,
  },
  {
    capabilityPath: '/email-signatures/deployments',
    label: 'Deployment',
    element: <DeploymentPage />,
  },
  {
    capabilityPath: '/email-signatures/templates',
    label: 'Templates',
    element: <TemplatesPage />,
  },
  {
    capabilityPath: '/email-signatures/bginfo',
    label: 'BG Info',
    element: <BGInfoPage />,
  },
  {
    capabilityPath: '/email-signatures/ooo',
    label: 'Out Of Office',
    element: <OutOfOfficePage />,
  },
  {
    capabilityPath: '/email-signatures/fonts',
    label: 'Font Library',
    element: <FontLibrary />,
  },
  {
    capabilityPath: '/email-signatures/health',
    label: 'Iris Deployment Health',
    element: <IrisDeploymentHealthPage />,
  },
  {
    capabilityPath: '/email-signatures/customers',
    label: 'Iris by Customer',
    element: <IrisCustomersPage />,
  },
  {
    capabilityPath: '/signatures/editor',
    label: 'Signature Editor',
    element: <SignatureRichTextEditor />,
  },
  {
    capabilityPath: '/signatures/editor/:signatureId',
    label: 'Signature Editor',
    // The route param must reach the editor: rendered bare it loaded nothing
    // and Save stayed disabled.
    element: <SignatureRichTextEditorForRoute />,
  },
];

function SignatureRichTextEditorForRoute() {
  const { signatureId } = useParams<{ signatureId: string }>();
  return <SignatureRichTextEditor signatureId={signatureId} />;
}
